import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useToast from '../../hooks/useToast';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import '../FullViewLayout.css';
import './ViewSaleModalStyles.css';
import apiService from '../../services/api';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import ConfirmModal from '../ConfirmModal';
import ContactPurchasesModal from './ContactPurchasesModal';

const DL = ({ label, value, span }) => (
  <div style={{ gridColumn: span ? 'span 2' : 'span 1' }}>
    <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
    <div style={{ fontWeight: 500, color: '#1e293b', fontSize: '0.92rem', wordBreak: 'break-word' }}>{value || <span style={{ color: '#94a3b8' }}>-</span>}</div>
  </div>
);
const SH = ({ children }) => (
  <div style={{ gridColumn: 'span 2', borderBottom: '1px solid #e2e8f0', paddingBottom: '0.35rem', marginTop: '0.5rem', marginBottom: '0.25rem', fontWeight: 700, fontSize: '0.82rem', color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{children}</div>
);
const Customers = ({ isActive, filterStatus, searchTerm, onSearchChange, onShowFilters }) => {
    const navigate = useNavigate();
    const { showSuccess, showError } = useToast();
    const [customers, setCustomers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, action: '', data: null });

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [totalEntries, setTotalEntries] = useState(0);
    const [lastPage, setLastPage] = useState(1);
    const [isClientPaginated, setIsClientPaginated] = useState(false);

    // Filter States removed (lifted to parent)

    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [openActionId, setOpenActionId] = useState(null);
    const [showExportDropdown, setShowExportDropdown] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showPayModal, setShowPayModal] = useState(false);
    const [showLedgerModal, setShowLedgerModal] = useState(false);
    const [showDocModal, setShowDocModal] = useState(false);
    const [showPurchasesModal, setShowPurchasesModal] = useState(false);
    const [payAmount, setPayAmount] = useState('');
    const [payNote, setPayNote] = useState('');
    const [docNote, setDocNote] = useState('');

    const handleExport = (format) => {
        if (!customers || customers.length === 0) {
            alert('No customers to export');
            setShowExportDropdown(false);
            return;
        }

        const headers = ['Contact ID', 'Business Name', 'Name', 'Email', 'Mobile', 'Tax Number', 'Credit Limit', 'Balance'];
        const rows = customers.map(c => [
            c.contact_id || '', c.business_name || '', c.name || '', c.email || '', c.mobile || '',
            c.tax_number || '', c.credit_limit || '0', c.balance || '0'
        ]);

        const title = 'Customers List';
        const filename = 'customers';

        if (format === 'CSV') exportToCSV(headers, rows, filename);
        else if (format === 'Excel') exportToExcel(headers, rows, filename);
        else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
        else if (format === 'Print') printPage();

        setShowExportDropdown(false);
    };

    const handlePrint = () => { printPage(); setShowExportDropdown(false); };
    const handleColumnVisibility = () => { alert('Column visibility settings'); setShowExportDropdown(false); };
    const handleExportPDF = () => { handleExport('PDF'); };

    useEffect(() => {
        if (isActive) {
            fetchCustomers();
        }
    }, [isActive, currentPage, perPage, searchTerm, filterStatus]);

    const fetchCustomers = async () => {
        setIsLoading(true);
        try {
            const data = await apiService.getContacts({
                type: 'customer',
                page: currentPage,
                per_page: perPage,
                name: searchTerm,
                status: filterStatus !== 'all' ? filterStatus : undefined
            });
            const customerList = Array.isArray(data) ? data : (data.data || []);
            setCustomers(customerList);

            const isArray = Array.isArray(data);
            setIsClientPaginated(isArray);

            if (isArray) {
                setTotalEntries(customerList.length);
                setLastPage(Math.ceil(customerList.length / perPage));
            } else {
                setTotalEntries(data.total || customerList.length);
                if (data.last_page) setLastPage(data.last_page);
                else if (data.meta && data.meta.last_page) setLastPage(data.meta.last_page);
                else setLastPage(Math.ceil((data.total || customerList.length) / perPage));
            }

        } catch (err) {
            console.error('Failed to load customers', err);
            setError('Failed to load customers');
        } finally {
            setIsLoading(false);
        }
    };
    // ... toggleActionMenu, useEffect (click outside), handleSort, getSortedCustomers ...
    const toggleActionMenu = (id) => {
        setOpenActionId(openActionId === id ? null : id);
    };

    const handleViewCustomer = (customer) => {
        if (!customer) return;
        setSelectedCustomer(customer);
        setShowViewModal(true);
        setOpenActionId(null);
    };

    const handleCloseModal = () => {
        setShowViewModal(false);
        setSelectedCustomer(null);
    };

    const handleEditCustomer = (id) => {
        navigate(`/contacts/customers/edit/${id}`);
    };

    const handleDeleteCustomer = (id) => {
        setOpenActionId(null);
        setConfirmModal({ isOpen: true, action: 'delete', data: id });
    };

    const handleDeactivate = (customer) => {
        setOpenActionId(null);
        setConfirmModal({ isOpen: true, action: 'deactivate', data: customer });
    };

    const confirmAction = async () => {
        const { action, data } = confirmModal;
        setConfirmModal({ isOpen: false, action: '', data: null });

        if (action === 'delete') {
            try {
                await apiService.deleteContact(data);
                fetchCustomers();
                showSuccess('Customer deleted successfully!');
            } catch (err) {
                showError('Failed to delete customer: ' + err.message);
            }
        } else if (action === 'deactivate') {
            const customer = data;
            const newStatus = customer.contact_status === 'inactive' ? 'active' : 'inactive';
            try {
                await apiService.updateContact(customer.id, { contact_status: newStatus });
                // Update local state immediately so it stays in the list with the updated status
                setCustomers(prev => prev.map(c => c.id === customer.id ? { ...c, contact_status: newStatus } : c));
                
                if (newStatus === 'inactive') {
                    showSuccess('Customer deactivated successfully!');
                } else {
                    showSuccess('Customer activated successfully!');
                }
            } catch (err) { 
                showError('Failed: ' + err.message); 
            }
        }
    };

    const openPayModal = (customer) => { setSelectedCustomer(customer); setPayAmount(''); setPayNote(''); setShowPayModal(true); setOpenActionId(null); };
    const openLedger = (customer) => { setSelectedCustomer(customer); setShowLedgerModal(true); setOpenActionId(null); };
    const openDocModal = (customer) => { setSelectedCustomer(customer); setDocNote(''); setShowDocModal(true); setOpenActionId(null); };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.action-dropdown-container')) {
                setOpenActionId(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedCustomers = (list) => {
        if (!sortConfig.key) return list;
        return [...list].sort((a, b) => {
            let aVal = a[sortConfig.key] || '';
            let bVal = b[sortConfig.key] || '';
            if (typeof aVal === 'string') aVal = aVal.toLowerCase();
            if (typeof bVal === 'string') bVal = bVal.toLowerCase();

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    return (
        <div className="full-view-inner">
            {/* Table Controls (Search, Entries) */}
            <div className="table-controls">
                <div className="table-controls-left">
                    <div className="entries-dropdown">
                        <span>Show</span>
                        <select value={perPage} onChange={(e) => {
                            setPerPage(Number(e.target.value));
                            setCurrentPage(1);
                        }}>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span>entries</span>
                    </div>
                </div>

                <div className="table-controls-right">
                    <div className="tabs-search-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input
                            type="text"
                            placeholder="Search Customers..."
                            value={searchTerm}
                            onChange={(e) => onSearchChange(e.target.value)}
                            className="tabs-search-input"
                        />
                        <button
                            className="filter-toggle-btn"
                            onClick={onShowFilters}
                            title="Open Filters"
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                            </svg>
                            Filters
                        </button>
                    </div>
                    <div className="export-dropdown-container" style={{ position: 'relative' }}>
                        <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            Export ▾
                        </button>
                        {showExportDropdown && (
                            <div className="export-dropdown-menu">
                                <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                                <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                                <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                                <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                            </div>
                        )}
                    </div>
                    <button className="control-btn-primary" onClick={() => navigate('/contacts/customers/add')}>
                        <span className="plus-icon">+</span> Add
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="products-table-container">
                    {isLoading ? (
                        <div className="loading-sales">
                            <h3>⏳ Loading Customers...</h3>
                            <p>Fetching customers data from server...</p>
                        </div>
                    ) : customers.length === 0 ? (
                        <div className="full-view-empty">
                            <h3>📊 No customers found</h3>
                            <p>Try adjusting filters or add a new customer.</p>
                        </div>
                    ) : (
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th>Action</th>
                                    {[
                                        { key: 'contact_id', label: 'Contact ID' },
                                        { key: 'business_name', label: 'Business Name' },
                                        { key: 'name', label: 'Name' },
                                        { key: 'email', label: 'Email', className: 'expand-column' },
                                        { key: 'mobile', label: 'Mobile' },
                                        { key: 'tax_number', label: 'Tax Number' },
                                        { key: 'credit_limit', label: 'Credit Limit' },
                                        { key: 'balance', label: 'Balance' }
                                    ].map(({ key, label, className }) => (
                                        <th key={key} className={`sortable ${className || ''}`} onClick={() => handleSort(key)}>
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                                                {label}
                                                {sortConfig.key === key ? (
                                                    sortConfig.direction === 'asc' ? (
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M18 15l-6-6-6 6" />
                                                        </svg>
                                                    ) : (
                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M6 9l6 6 6-6" />
                                                        </svg>
                                                    )
                                                ) : (
                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M7 15l5 5 5-5" />
                                                        <path d="M7 9l5-5 5 5" />
                                                    </svg>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {customers.length === 0 ? (
                                    <tr><td colSpan="9" style={{ textAlign: 'center', padding: '2rem' }}>No customers found.</td></tr>
                                ) : (
                                    (() => {
                                        const sorted = getSortedCustomers(customers);
                                        const displayList = isClientPaginated 
                                            ? sorted.slice((currentPage - 1) * perPage, currentPage * perPage) 
                                            : sorted;
                                        return displayList.map(customer => (
                                            <tr key={customer.id}>
                                                <td className="actions-cell">
                                                    <div className="action-dropdown-container">
                                                        <button className="action-btn-trigger" onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleActionMenu(customer.id);
                                                        }}>
                                                            Actions <span className="arrow-down">▼</span>
                                                        </button>
                                                        {openActionId === customer.id && (
                                                            <div className="action-dropdown-menu">
                                                                <button onClick={() => openPayModal(customer)}><i className="fa fa-credit-card" style={{ marginRight: '0.5rem', color: '#6366f1' }}></i> Pay</button>
                                                                <button onClick={() => handleViewCustomer(customer)}><i className="fa fa-eye" style={{ marginRight: '0.5rem', color: '#3b82f6' }}></i> View</button>
                                                                <button onClick={() => handleEditCustomer(customer.id)}><i className="fa fa-edit" style={{ marginRight: '0.5rem', color: '#10b981' }}></i> Edit</button>
                                                                <button onClick={() => handleDeleteCustomer(customer.id)}><i className="fa fa-trash" style={{ marginRight: '0.5rem', color: '#ef4444' }}></i> Delete</button>
                                                                <button onClick={() => handleDeactivate(customer)}><i className="fa fa-power-off" style={{ marginRight: '0.5rem', color: '#f59e0b' }}></i> {customer.contact_status === 'inactive' ? 'Activate' : 'Deactivate'}</button>
                                                                <hr style={{ margin: '4px 0', border: 'none', borderTop: '1px solid #e2e8f0' }} />
                                                                <button onClick={() => openLedger(customer)}><i className="fa fa-book" style={{ marginRight: '0.5rem', color: '#8b5cf6' }}></i> Ledger</button>
                                                                <button onClick={() => { setSelectedCustomer(customer); setShowPurchasesModal(true); setOpenActionId(null); }}><i className="fa fa-shopping-bag" style={{ marginRight: '0.5rem', color: '#0891b2' }}></i> Purchases</button>
                                                                <button onClick={() => openDocModal(customer)}><i className="fa fa-paperclip" style={{ marginRight: '0.5rem', color: '#dc2626' }}></i> Documents &amp; Note</button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>{customer.contact_id}</td>
                                                <td>
                                                    <div style={{ fontWeight: '500' }}>{customer.supplier_business_name || customer.business_name || '--'}</div>
                                                    {customer.contact_status === 'inactive' && <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: '700', marginTop: '4px' }}>(Deactivated)</div>}
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: '500' }}>{customer.name || '--'}</div>
                                                    {customer.contact_status === 'inactive' && <div style={{ color: '#ef4444', fontSize: '0.8rem', fontWeight: '700', marginTop: '4px' }}>(Deactivated)</div>}
                                                </td>
                                                <td className="expand-column">{customer.email || '--'}</td>
                                                <td>{customer.mobile}</td>
                                                <td>{customer.tax_number || '--'}</td>
                                                <td>{customer.credit_limit ? `$${Number(customer.credit_limit).toFixed(2)}` : '--'}</td>
                                                <td>{customer.balance ? `$${Number(customer.balance).toFixed(2)}` : '$0.00'}</td>
                                            </tr>
                                        ));
                                    })()
                                )}
                            </tbody>
                        </table>
                    )}
            </div>

            {/* Pagination */}
            {customers.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                    <div>
                        Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalEntries)} of {totalEntries} entries
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>
                            Page {currentPage} of {lastPage || 1}
                        </span>
                        <button
                            className="pagination-btn"
                            onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))}
                            disabled={currentPage === lastPage}
                        >
                            Next
                        </button>
                    </div>
                </div>
            )}

            {/* ── View Modal ── */}
            {showViewModal && selectedCustomer && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-container invoice-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>View Customer — <span style={{ color: '#6366f1' }}>{selectedCustomer.supplier_business_name || selectedCustomer.business_name || selectedCustomer.name}</span></h2>
                            <button className="modal-close-btn" onClick={handleCloseModal}>✕</button>
                        </div>
                        <div className="modal-content invoice-content" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                {/* Main info row */}
                                <div className="customer-service-section">
                                    <div className="customer-info-box">
                                        <h3 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '0.75rem', borderBottom: '1px solid var(--etpos-border)', paddingBottom: '0.25rem', color: 'var(--etpos-primary)' }}>Basic Info</h3>
                                        <div className="info-item">
                                            <strong>Contact ID</strong>
                                            <div>{selectedCustomer.contact_id || '--'}</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Full Name</strong>
                                            <div>{selectedCustomer.name || '--'}</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Business Name</strong>
                                            <div>{selectedCustomer.supplier_business_name || selectedCustomer.business_name || '--'}</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Tax Number</strong>
                                            <div>{selectedCustomer.tax_number || '--'}</div>
                                        </div>
                                    </div>
                                    <div className="service-info-box">
                                        <h3 style={{ fontSize: '1.05rem', fontWeight: '600', marginBottom: '0.75rem', borderBottom: '1px solid var(--etpos-border)', paddingBottom: '0.25rem', color: 'var(--etpos-primary)' }}>Account Status</h3>
                                        <div className="info-item">
                                            <strong>Status</strong>
                                            <div>
                                                <span style={{ 
                                                    padding: '0.2rem 0.6rem', 
                                                    borderRadius: '4px', 
                                                    fontSize: '0.75rem', 
                                                    fontWeight: '700',
                                                    backgroundColor: selectedCustomer.contact_status === 'inactive' ? '#fee2e2' : '#dcfce7',
                                                    color: selectedCustomer.contact_status === 'inactive' ? '#991b1b' : '#166534'
                                                }}>
                                                    {(selectedCustomer.contact_status || 'active').toUpperCase()}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Type / Sub Type</strong>
                                            <div>{selectedCustomer.type || 'Customer'} {selectedCustomer.sub_type ? `/ ${selectedCustomer.sub_type}` : ''}</div>
                                        </div>
                                        <div className="info-item" style={{ marginTop: '0.5rem' }}>
                                            <strong>Added On</strong>
                                            <div>{selectedCustomer.created_at ? new Date(selectedCustomer.created_at).toLocaleDateString() : '--'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Contact Details */}
                                <div className="invoice-section">
                                    <h3>Contact Details</h3>
                                    <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                                        <div className="info-item">
                                            <strong>Mobile</strong>
                                            <div>{selectedCustomer.mobile || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Alternate Number</strong>
                                            <div>{selectedCustomer.alternate_number || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Landline</strong>
                                            <div>{selectedCustomer.landline || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Email</strong>
                                            <div>{selectedCustomer.email || '--'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Financial Info */}
                                <div className="invoice-section">
                                    <h3>Financial Information</h3>
                                    <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                                        <div className="info-item">
                                            <strong>Opening Balance</strong>
                                            <div>{selectedCustomer.opening_balance != null ? `$${Number(selectedCustomer.opening_balance).toFixed(2)}` : '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Advance Balance</strong>
                                            <div>{selectedCustomer.advance_balance != null ? `$${Number(selectedCustomer.advance_balance).toFixed(2)}` : '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Current Balance</strong>
                                            <div style={{ fontWeight: 700, color: selectedCustomer.balance > 0 ? '#ef4444' : '#166534' }}>
                                                {`$${Number(selectedCustomer.balance || 0).toFixed(2)}`}
                                            </div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Credit Limit</strong>
                                            <div>{selectedCustomer.credit_limit != null ? `$${Number(selectedCustomer.credit_limit).toFixed(2)}` : '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Total Sell Due</strong>
                                            <div>{selectedCustomer.sell_due != null ? `$${Number(selectedCustomer.sell_due).toFixed(2)}` : (selectedCustomer.total_sell_due != null ? `$${Number(selectedCustomer.total_sell_due).toFixed(2)}` : '--')}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Total Sell Return Due</strong>
                                            <div>{selectedCustomer.sell_return != null ? `$${Number(selectedCustomer.sell_return).toFixed(2)}` : (selectedCustomer.total_sell_return_due != null ? `$${Number(selectedCustomer.total_sell_return_due).toFixed(2)}` : '--')}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Pay Term</strong>
                                            <div>{selectedCustomer.pay_term_number ? `${selectedCustomer.pay_term_number} ${selectedCustomer.pay_term_type || ''}` : '--'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Address Section */}
                                <div className="invoice-section">
                                    <h3>Address</h3>
                                    <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                                        <div className="info-item">
                                            <strong>Address Line 1</strong>
                                            <div>{selectedCustomer.address_line_1 || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Address Line 2</strong>
                                            <div>{selectedCustomer.address_line_2 || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>City</strong>
                                            <div>{selectedCustomer.city || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>State</strong>
                                            <div>{selectedCustomer.state || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Country</strong>
                                            <div>{selectedCustomer.country || '--'}</div>
                                        </div>
                                        <div className="info-item">
                                            <strong>Zip Code</strong>
                                            <div>{selectedCustomer.zip_code || '--'}</div>
                                        </div>
                                    </div>
                                </div>

                                {/* Custom Fields Section */}
                                {(selectedCustomer.custom_field1 || selectedCustomer.custom_field2 || selectedCustomer.custom_field3 || selectedCustomer.custom_field4 || selectedCustomer.custom_field5) && (
                                    <div className="invoice-section">
                                        <h3>Custom Fields</h3>
                                        <div className="shipping-info-box" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
                                            {selectedCustomer.custom_field1 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 1</strong>
                                                    <div>{selectedCustomer.custom_field1}</div>
                                                </div>
                                            )}
                                            {selectedCustomer.custom_field2 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 2</strong>
                                                    <div>{selectedCustomer.custom_field2}</div>
                                                </div>
                                            )}
                                            {selectedCustomer.custom_field3 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 3</strong>
                                                    <div>{selectedCustomer.custom_field3}</div>
                                                </div>
                                            )}
                                            {selectedCustomer.custom_field4 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 4</strong>
                                                    <div>{selectedCustomer.custom_field4}</div>
                                                </div>
                                            )}
                                            {selectedCustomer.custom_field5 && (
                                                <div className="info-item">
                                                    <strong>Custom Field 5</strong>
                                                    <div>{selectedCustomer.custom_field5}</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="modal-btn close-btn" style={{ background: '#6366f1', color: 'white' }} onClick={() => { handleCloseModal(); handleEditCustomer(selectedCustomer.id); }}>Edit</button>
                            <button className="modal-btn close-btn" onClick={handleCloseModal}>Close</button>
                        </div>
                    </div>
                </div>
            )}

        {/* Pay Modal */}
        {showPayModal && selectedCustomer && (
            <div className="modal-overlay" onClick={() => setShowPayModal(false)}>
                <div className="modal-container" style={{ maxWidth: '700px', width: '95%', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                        <h2>Add payment</h2>
                        <button className="modal-close-btn" onClick={() => setShowPayModal(false)}>✕</button>
                    </div>
                    <div style={{ padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        
                        {/* Top Info Cards */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '1rem', fontSize: '0.9rem', color: '#1e293b' }}>
                                <div style={{ marginBottom: '0.25rem' }}><strong>Customer:</strong> {selectedCustomer.name || '--'}</div>
                                <div><strong>Business:</strong> {selectedCustomer.supplier_business_name || selectedCustomer.business_name || '--'}</div>
                            </div>
                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '1rem', fontSize: '0.9rem', color: '#1e293b', lineHeight: '1.5' }}>
                                <div><strong>Total Sell:</strong> ${Number(selectedCustomer.total_sell || 0).toFixed(2)}</div>
                                <div><strong>Total Paid:</strong> ${Number(selectedCustomer.total_paid || 0).toFixed(2)}</div>
                                <div><strong>Total Sell Due:</strong> ${Number(selectedCustomer.total_sell_due || selectedCustomer.sell_due || 0).toFixed(2)}</div>
                                <div><strong>Credit Limit:</strong> ${selectedCustomer.credit_limit ? Number(selectedCustomer.credit_limit).toFixed(2) : '0.00'}</div>
                                <div><strong>Opening Balance:</strong> ${Number(selectedCustomer.opening_balance || 0).toFixed(2)}</div>
                                <div><strong>Opening Balance Due:</strong> ${Number(selectedCustomer.opening_balance || 0).toFixed(2)}</div>
                            </div>
                        </div>

                        {/* Form Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', display: 'block', marginBottom: '0.4rem' }}>Payment Method:*</label>
                                <div style={{ display: 'flex', border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden' }}>
                                    <span style={{ padding: '0.5rem', background: '#f1f5f9', borderRight: '1px solid #cbd5e1', color: '#64748b' }}><i className="fa fa-money-bill-wave"></i></span>
                                    <select style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'white' }}>
                                        <option>Cash</option>
                                        <option>Card</option>
                                        <option>Check</option>
                                        <option>Bank Transfer</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', display: 'block', marginBottom: '0.4rem' }}>Paid on:*</label>
                                <div style={{ display: 'flex', border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden' }}>
                                    <span style={{ padding: '0.5rem', background: '#f1f5f9', borderRight: '1px solid #cbd5e1', color: '#64748b' }}><i className="fa fa-calendar"></i></span>
                                    <input type="datetime-local" defaultValue={new Date().toISOString().slice(0, 16)} style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none' }} />
                                </div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', display: 'block', marginBottom: '0.4rem' }}>Amount:*</label>
                                <div style={{ display: 'flex', border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden' }}>
                                    <span style={{ padding: '0.5rem', background: '#f1f5f9', borderRight: '1px solid #cbd5e1', color: '#64748b' }}><i className="fa fa-money-bill"></i></span>
                                    <input type="number" step="0.01" value={payAmount} onChange={e => setPayAmount(e.target.value)} style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none' }} />
                                </div>
                            </div>
                        </div>

                        {/* Bottom Form Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem' }}>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', display: 'block', marginBottom: '0.4rem' }}>Attach Document:</label>
                                <input type="file" style={{ width: '100%', fontSize: '0.85rem' }} />
                                <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.5rem' }}>Allowed File: .pdf, .csv, .zip, .doc, .docx, .jpeg, .jpg, .png</div>
                            </div>
                            <div>
                                <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', display: 'block', marginBottom: '0.4rem' }}>Payment Account:</label>
                                <div style={{ display: 'flex', border: '1px solid #cbd5e1', borderRadius: '4px', overflow: 'hidden' }}>
                                    <span style={{ padding: '0.5rem', background: '#f1f5f9', borderRight: '1px solid #cbd5e1', color: '#64748b' }}><i className="fa fa-university"></i></span>
                                    <select style={{ flex: 1, padding: '0.5rem', border: 'none', outline: 'none', background: 'white' }}>
                                        <option>None</option>
                                        <option>Cash on Hand</option>
                                        <option>New Bank Account</option>
                                        <option>NSF Return</option>
                                        <option>Axis bank</option>
                                        <option>CASH ON HAND</option>
                                    </select>
                                </div>
                                <div style={{ marginTop: '1rem' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1e293b', display: 'block', marginBottom: '0.4rem' }}>Payment Note:</label>
                                    <textarea rows={2} value={payNote} onChange={e => setPayNote(e.target.value)} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }} placeholder="Optional note" />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer" style={{ padding: '1.25rem 2rem', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                        <button style={{ padding: '0.6rem 1.75rem', background: '#4f46e5', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }} onClick={() => { alert('Payment saved'); setShowPayModal(false); }}>Save</button>
                        <button style={{ padding: '0.6rem 1.75rem', background: '#334155', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 700 }} onClick={() => setShowPayModal(false)}>Close</button>
                    </div>
                </div>
            </div>
        )}

        {/* Ledger Modal */}
        {showLedgerModal && selectedCustomer && (
            <div className="modal-overlay" onClick={() => setShowLedgerModal(false)}>
                <div className="modal-container" style={{ maxWidth: '700px', width: '95%', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                    <div className="modal-header"><h2>Ledger - {selectedCustomer.name}</h2><button className="modal-close-btn" onClick={() => setShowLedgerModal(false)}>x</button></div>
                    <div style={{ padding: '1.5rem 2rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '0.75rem' }}><div style={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: 700, textTransform: 'uppercase' }}>Total Sales</div><div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#15803d' }}>0.00</div></div>
                            <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '0.75rem' }}><div style={{ fontSize: '0.72rem', color: '#2563eb', fontWeight: 700, textTransform: 'uppercase' }}>Total Paid</div><div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1d4ed8' }}>0.00</div></div>
                            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '0.75rem' }}><div style={{ fontSize: '0.72rem', color: '#dc2626', fontWeight: 700, textTransform: 'uppercase' }}>Balance</div><div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#b91c1c' }}>{Number(selectedCustomer.balance || 0).toFixed(2)}</div></div>
                        </div>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                            <thead><tr style={{ background: '#f8fafc' }}><th style={{ padding: '0.6rem', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>Date</th><th style={{ padding: '0.6rem', borderBottom: '1px solid #e2e8f0' }}>Type</th><th style={{ padding: '0.6rem', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Amount</th><th style={{ padding: '0.6rem', textAlign: 'right', borderBottom: '1px solid #e2e8f0' }}>Balance</th></tr></thead>
                            <tbody><tr><td colSpan={4} style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>No transactions found</td></tr></tbody>
                        </table>
                    </div>
                    <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '1rem 2rem', display: 'flex', justifyContent: 'flex-end' }}>
                        <button style={{ padding: '0.5rem 1.25rem', background: '#64748b', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }} onClick={() => setShowLedgerModal(false)}>Close</button>
                    </div>
                </div>
            </div>
        )}

        {/* Documents and Note Modal */}
        {showDocModal && selectedCustomer && (
            <div className="modal-overlay" onClick={() => setShowDocModal(false)}>
                <div className="modal-container" style={{ maxWidth: '500px', width: '95%' }} onClick={e => e.stopPropagation()}>
                    <div className="modal-header"><h2>Documents and Note - {selectedCustomer.name}</h2><button className="modal-close-btn" onClick={() => setShowDocModal(false)}>x</button></div>
                    <div style={{ padding: '1.5rem 2rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div><label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Note</label>
                            <textarea rows={4} value={docNote} onChange={e => setDocNote(e.target.value)} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: '0.9rem', resize: 'vertical' }} placeholder="Add a note..." /></div>
                        <div><label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }}>Attach Document</label>
                            <input type="file" style={{ width: '100%' }} /></div>
                    </div>
                    <div className="modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '1rem 2rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                        <button style={{ padding: '0.5rem 1.25rem', background: '#6366f1', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }} onClick={() => { alert('Saved (demo)'); setShowDocModal(false); }}>Save</button>
                        <button style={{ padding: '0.5rem 1.25rem', background: '#64748b', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }} onClick={() => setShowDocModal(false)}>Cancel</button>
                    </div>
                </div>
            </div>
        )}

        {/* ── Purchases Modal ── */}
        {showPurchasesModal && selectedCustomer && (
            <ContactPurchasesModal
                type="customer"
                contact={selectedCustomer}
                onClose={() => setShowPurchasesModal(false)}
            />
        )}

        <ConfirmModal
            isOpen={confirmModal.isOpen}
            title={confirmModal.action === 'delete' ? 'Delete Customer' : confirmModal.data?.contact_status === 'inactive' ? 'Activate Customer' : 'Deactivate Customer'}
            message={confirmModal.action === 'delete' ? 'Are you sure you want to delete this customer?' : `Are you sure you want to ${confirmModal.data?.contact_status === 'inactive' ? 'activate' : 'deactivate'} this customer?`}
            onConfirm={confirmAction}
            onCancel={() => setConfirmModal({ isOpen: false, action: '', data: null })}
            confirmText={confirmModal.action === 'delete' ? 'Delete' : confirmModal.data?.contact_status === 'inactive' ? 'Activate' : 'Deactivate'}
        />
        </div>
    );
};

export default Customers;
